// SPDX-License-Identifier: AGPL-3.0-or-later

import * as AccessibilityCommands from '@app/features/accessibility/commands/AccessibilityCommands';
import Accessibility, {
	ZOOM_LEVEL_MARKERS,
	ZOOM_LEVEL_MAX,
	ZOOM_LEVEL_MIN,
} from '@app/features/accessibility/state/Accessibility';
import Keybind from '@app/features/input/state/Keybind';
import {formatKeyCombo} from '@app/features/input/utils/KeybindUtils';
import {ComponentDispatch} from '@app/features/platform/utils/ComponentBus';
import {Button} from '@app/features/ui/button/Button';
import {Slider} from '@app/features/ui/components/Slider';
import {shouldWarnAboutFirefoxWebZoomShortcuts} from '@app/features/ui/utils/AppZoomKeybindUtils';
import styles from '@app/features/user/components/modals/tabs/appearance_tab/ScalingTab.module.css';
import {Trans, useLingui} from '@lingui/react/macro';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';
export const FontSizeTabContent: React.FC = observer(() => {
	const fontSize = Accessibility.fontSize;
	return (
		<Slider
			defaultValue={fontSize}
			factoryDefaultValue={16}
			markers={[12, 14, 15, 16, 18, 20, 24]}
			stickToMarkers={true}
			onValueChange={(value) => AccessibilityCommands.update({fontSize: value})}
			onMarkerRender={(value) => `${value}px`}
			showResetButton={true}
		/>
	);
});
export const AppZoomLevelTabContent: React.FC = observer(() => {
	const zoomLevel = Accessibility.zoomLevel;
	const zoomPercent = Math.round(zoomLevel * 100);
	const markers = ZOOM_LEVEL_MARKERS.map((step) => Math.round(step * 100));
	const isOnMarker = markers.includes(zoomPercent);
	const {t} = useLingui();
	const handleResetZoom = () => {
		AccessibilityCommands.update({zoomLevel: 1.0});
	};
	const handleOpenKeybinds = () => {
		ComponentDispatch.dispatch('USER_SETTINGS_TAB_SELECT', {tab: 'keybinds'});
	};
	return (
		<div className={styles.container}>
			<Slider
				defaultValue={zoomPercent}
				factoryDefaultValue={100}
				minValue={Math.round(ZOOM_LEVEL_MIN * 100)}
				maxValue={Math.round(ZOOM_LEVEL_MAX * 100)}
				step={1}
				markers={markers}
				stickToMarkers={false}
				asValueChanges={() => {}}
				onValueChange={(value) => AccessibilityCommands.update({zoomLevel: value / 100})}
				onMarkerRender={(value) => `${value}%`}
				onValueRender={(value) => <Trans>{value}%</Trans>}
			/>
			<div className={styles.actions}>
				<Button variant="secondary" small fitContent onClick={handleResetZoom}>
					{t`Reset Zoom`}
				</Button>
				<Button variant="secondary" small fitContent onClick={handleOpenKeybinds}>
					{t`Configure keyboard shortcuts`}
				</Button>
				{!isOnMarker && <span className={styles.currentValue}>{t`Current: ${zoomPercent}%`}</span>}
			</div>
		</div>
	);
});
export function useAppZoomLevelDescription(): string {
	const {t} = useLingui();
	return useMemo(() => {
		if (shouldWarnAboutFirefoxWebZoomShortcuts()) {
			return t`Adjust the overall zoom level of the app. Firefox keeps the standard browser zoom shortcuts, so use the slider here or rebind these actions in Keyboard Shortcuts.`;
		}
		const zoomIn = formatKeyCombo(Keybind.getByAction('system_zoom_in').combo);
		const zoomOut = formatKeyCombo(Keybind.getByAction('system_zoom_out').combo);
		return t`Adjust the overall zoom level of the app. Use ${zoomIn} / ${zoomOut} to adjust quickly.`;
	}, [t]);
}
